import type {
	NewsImageView,
	NewsItemAdd,
	NewsItemView,
	NewsLanguageCoverage,
	NewsStatus,
} from "@slfinrtl/ui-bff-routes"
import {
	addNewsItemRoute,
	getNewsItemRoute,
	publishNewsItemRoute,
	reviewNewsItemRoute,
	updateNewsItemRoute,
} from "@slfinrtl/ui-bff-routes"
import type { AxiosInstance } from "axios"
import dayjs from "dayjs"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"
import { useIntl } from "react-intl"
import type { MessageDescriptor } from "react-intl"
import { extractErrorMessage } from "src/common/errorHandling/extractErrorMessage/extractErrorMessage"
import { isUiErrorResponse } from "src/common/errorHandling/isUIErrorResponse/isUIErrorResponse"
import { useErrorBanner } from "src/context/errorBanner/useErrorBanner"
import { getAxiosInstance } from "../../../dealershipList/common/getAxiosInstance/getAxiosInstance"
import { RequestContext } from "../../../dealershipList/requestContext/RequestContext"
import { newsEditorMessages as messages } from "../../components/newsEditor.messages"

interface UseNewsEditorProps {
	/** a new entry is edited if undefined */
	newsItemId?: string
	/** called after the entry has been saved, sent to review or published */
	onSaved: () => void
}

interface UseNewsEditorReturn {
	form: UseFormReturn<NewsItemAdd>
	/** the saved images of the entry, empty for a new entry or while it is loading */
	images: NewsImageView[]
	/** true while an existing entry is being loaded, or if it could not be loaded */
	isLoading: boolean
	/** true while a request that changes the entry is running */
	isSaving: boolean
	/** true while an image is being uploaded, the entry must not be saved or transitioned meanwhile */
	isUploadingImages: boolean
	/** empty as long as the entry is a draft or while it is loading */
	languageCoverage: NewsLanguageCoverage[]
	onPublish: () => Promise<void>
	onSave: () => Promise<void>
	/** saves the entry and sends it to review, the confirmation has to be asked for before */
	onSendToReview: () => Promise<void>
	setIsUploadingImages: (isUploading: boolean) => void
	/** undefined for a new entry or while the entry is loading */
	status: NewsStatus | undefined
}

const defaultPublicationDelayInWeeks = 2

const getEmptyFormValues = (): NewsItemAdd => ({
	content: "",
	imageIds: [],
	marketplaceKeys: [],
	plannedPublicationDate: dayjs().add(defaultPublicationDelayInWeeks, "week").format("YYYY-MM-DD"),
	title: "",
})

const formFields: string[] = Object.keys(getEmptyFormValues())

const toFormValues = (view: NewsItemView): NewsItemAdd => ({
	content: view.content,
	imageIds: view.images.map(({ id }) => id),
	marketplaceKeys: view.marketplaceKeys,
	plannedPublicationDate: view.plannedPublicationDate,
	title: view.title,
})

export const useNewsEditor = ({ newsItemId, onSaved }: UseNewsEditorProps): UseNewsEditorReturn => {
	const { environment, getAuthorizationHeader } = useContext(RequestContext)
	const { showError } = useErrorBanner()
	const { formatMessage } = useIntl()
	const [view, setView] = useState<NewsItemView | undefined>()
	const emptyFormValues = useMemo(() => getEmptyFormValues(), [])
	// Fields registered with shouldUnregister restore their value from these options when they are mounted again,
	// so they have to contain the loaded entry, otherwise a required field is empty on submit.
	const form = useForm<NewsItemAdd>({ defaultValues: view ? toFormValues(view) : emptyFormValues })
	const [isSaving, setIsSaving] = useState(false)
	const [isUploadingImages, setIsUploadingImages] = useState(false)
	// A new entry that has been created is updated by a retry, e.g. after sending it to review has failed.
	const [persistedNewsItemId, setPersistedNewsItemId] = useState(newsItemId)

	const showRequestError = useCallback(
		(error: unknown, fallbackMessage: MessageDescriptor) => {
			const errorMessage = extractErrorMessage(error)
			showError(errorMessage.message ? errorMessage : { message: formatMessage(fallbackMessage) })
		},
		[formatMessage, showError],
	)

	const { reset, setError } = form
	// returns true if every validation error could be shown at a field of the form
	const setValidationErrors = useCallback(
		(error: unknown): boolean => {
			const validationErrors = isUiErrorResponse(error) ? error.response.data.validationErrors : undefined
			if (!validationErrors?.length) {
				return false
			}
			const messagesByField = new Map<string, string[]>()
			for (const { key, message } of validationErrors) {
				const field = key.split(".")[0]
				messagesByField.set(field, [...(messagesByField.get(field) ?? []), message])
			}
			for (const [field, fieldMessages] of messagesByField) {
				if (formFields.includes(field)) {
					setError(field as keyof NewsItemAdd, { message: fieldMessages.join(" "), type: "server" })
				}
			}
			return [...messagesByField.keys()].every((field) => formFields.includes(field))
		},
		[setError],
	)

	useEffect(() => {
		if (!newsItemId) {
			return
		}
		let isCancelled = false

		getAxiosInstance({ environment, getAuthorizationHeader })
			.then((axiosInstance) =>
				axiosInstance[getNewsItemRoute.method]<NewsItemView>(getNewsItemRoute.buildUrl({ newsItemId })),
			)
			.then(({ data }) => {
				if (!isCancelled) {
					setView(data)
					reset(toFormValues(data))
				}
			})
			.catch((error: unknown) => showRequestError(error, messages.errorLoadingEntry))

		return () => {
			isCancelled = true
		}
	}, [environment, getAuthorizationHeader, reset, showRequestError, newsItemId])

	const save = async (axiosInstance: AxiosInstance, values: NewsItemAdd): Promise<string> => {
		if (persistedNewsItemId) {
			await axiosInstance[updateNewsItemRoute.method]<NewsItemView>(
				updateNewsItemRoute.buildUrl({ newsItemId: persistedNewsItemId }),
				values,
			)
			return persistedNewsItemId
		}
		const { data } = await axiosInstance[addNewsItemRoute.method]<NewsItemView>(
			addNewsItemRoute.buildUrl({}),
			values,
		)
		setPersistedNewsItemId(data.id)
		return data.id
	}

	const submit = async (
		request: (axiosInstance: AxiosInstance) => Promise<void>,
		fallbackMessage: MessageDescriptor,
	) => {
		setIsSaving(true)
		try {
			await request(await getAxiosInstance({ environment, getAuthorizationHeader }))
			onSaved()
		} catch (error) {
			if (!setValidationErrors(error)) {
				showRequestError(error, fallbackMessage)
			}
		} finally {
			setIsSaving(false)
		}
	}

	const onSave = form.handleSubmit((values) =>
		submit(async (axiosInstance) => {
			await save(axiosInstance, values)
		}, messages.errorSavingEntry),
	)

	const onSendToReview = form.handleSubmit((values) =>
		submit(async (axiosInstance) => {
			const savedNewsItemId = await save(axiosInstance, values)
			await axiosInstance[reviewNewsItemRoute.method]<NewsItemView>(
				reviewNewsItemRoute.buildUrl({ newsItemId: savedNewsItemId }),
			)
		}, messages.errorSendingToReview),
	)

	const onPublish = async () => {
		if (!newsItemId) {
			return
		}
		await submit(async (axiosInstance) => {
			await axiosInstance[publishNewsItemRoute.method]<NewsItemView>(
				publishNewsItemRoute.buildUrl({ newsItemId }),
			)
		}, messages.errorPublishing)
	}

	return {
		form,
		images: view?.images ?? [],
		isLoading: !!newsItemId && !view,
		isSaving,
		isUploadingImages,
		languageCoverage: view?.languageCoverage ?? [],
		onPublish,
		onSave,
		onSendToReview,
		setIsUploadingImages,
		status: view?.status,
	}
}
